import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';

/**
 * Genera un sonido sintético y elegante de campana/aviso usando la Web Audio API nativa.
 * Funciona offline y no requiere cargar archivos MP3 externos.
 */
function playChimeSound() {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();

    // Tono 1: Campana aguda cristalina (A5 -> E6)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, ctx.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
    gain1.gain.setValueAtTime(0.35, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start();
    osc1.stop(ctx.currentTime + 0.6);

    // Tono 2: Armónico cálido de resonancia (D6)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(1174.66, ctx.currentTime + 0.08);
    gain2.gain.setValueAtTime(0.28, ctx.currentTime + 0.08);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.08);
    osc2.stop(ctx.currentTime + 0.9);
  } catch {
    // Si la política de audio del navegador lo bloquea sin interacción previa, se ignora silenciosamente
  }
}

/**
 * useWebOrderListener
 * Hook en segundo plano que consulta pedidos web realizados desde la PWA en la nube
 * cada 7 segundos para importarlos al POS local de XAMPP.
 */
export function useWebOrderListener(intervalMs = 7000) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isPollingRef = useRef(false);

  const pollWebOrders = useCallback(async () => {
    // Solo consultar si hay sesión activa y no hay otra petición en curso
    if (!token || isPollingRef.current || !navigator.onLine) {
      return;
    }

    isPollingRef.current = true;
    try {
      const response = await api.post('/cloud-sync/pull-web-orders');
      const data = response.data;

      if (data && data.success && data.imported > 0) {
        // 1. Reproducir sonido de atención en el computador de caja / cocina
        playChimeSound();

        // 2. Notificar visualmente al personal distinguiendo posibles duplicados por desconexión
        const orders = data.orders || [];
        const duplicates = orders.filter((o: any) => o.is_potential_duplicate);
        const regularOrders = orders.filter((o: any) => !o.is_potential_duplicate);

        if (duplicates.length > 0) {
          for (const dup of duplicates) {
            const mesaLabel = dup.table_number ? `Mesa ${dup.table_number}` : 'Para llevar';
            toast.warn(
              `⚠️ ${mesaLabel}: Pedido web recibido tras corte de internet. Como la mesa ya tenía comanda abierta por el mesero, NO se imprimió en cocina para evitar duplicidad.`,
              {
                autoClose: 14000,
                icon: '⚠️',
              }
            );
          }
        }

        if (regularOrders.length === 1) {
          const o = regularOrders[0];
          const mesaLabel = o.table_number ? `Mesa ${o.table_number}` : 'Para llevar';
          toast.success(`🔔 ¡Nuevo pedido web recibido en ${mesaLabel}!`, {
            autoClose: 8000,
            icon: '🍦',
          });
        } else if (regularOrders.length > 1) {
          toast.success(`🔔 ¡${regularOrders.length} nuevos pedidos web recibidos desde la nube!`, {
            autoClose: 8000,
            icon: '🍦',
          });
        }

        // 3. Actualizar cachés de React Query para que las mesas y órdenes se actualicen en vivo
        queryClient.invalidateQueries({ queryKey: ['tables'] });
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['active-order'] });
        queryClient.invalidateQueries({ queryKey: ['kpi'] });
      }
    } catch {
      // Ignoramos fallos transitorios de red para no interrumpir la experiencia del cajero
    } finally {
      isPollingRef.current = false;
    }
  }, [token, queryClient]);

  useEffect(() => {
    if (!token) return;

    // Ejecutar una consulta inicial a los 2 segundos de montar la app
    const initialTimer = setTimeout(() => {
      pollWebOrders();
    }, 2000);

    // Bucle periódico cada intervalMs
    const interval = setInterval(() => {
      pollWebOrders();
    }, intervalMs);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [token, intervalMs, pollWebOrders]);
}
